// Jun-2026 (owner-approved) — Day-wise Expenditure & Work Done feed on the
// Operations Dashboard. Vertical scroller of day cards (newest first) that
// expand to per-project rows. Expense = material consumption + service PI
// (ops POs, invoice date) + labour attendance amount; work done = net BOQ
// progress deltas ex-GST. Search + client filter apply inside every day.
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { CalendarDays, ChevronDown, ChevronRight, Search } from "lucide-react";

const inr = (v) => (v == null ? "•••" : formatINR(v));

const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
};

const Chip = ({ label, value, tone, testid }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${tone}`} data-testid={testid}>
    {label}: <span className="tabular-nums">{value}</span>
  </span>
);

export default function OpsDaywiseFeed() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [client, setClient] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    setData(null);
    api.get("/ops-dashboard/daywise", { params: { days } })
      .then((r) => {
        setData(r.data);
        const first = (r.data.days || []).find((d) => d.has_activity);
        if (first) setOpen({ [first.date]: true });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load day-wise feed"));
  }, [days]);

  const filteredDays = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return (data.days || []).map((d) => {
      const rows = (d.projects || []).filter((p) =>
        (!client || p.client_name === client)
        && (!s || `${p.project_code} ${p.project_name}`.toLowerCase().includes(s)));
      const sum = (k) => rows.some((r) => r[k] == null) && rows.length ? null
        : rows.reduce((a, r) => a + (r[k] || 0), 0);
      const totals = (client || s)
        ? { material: sum("material"), service_pi: sum("service_pi"), labour: sum("labour"),
            expense_total: sum("expense_total"), work_done: sum("work_done") }
        : d.totals;
      return { ...d, rows, totals, shown: rows.length };
    });
  }, [data, q, client]);

  if (err) return <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2" data-testid="opsday-error">{err}</div>;
  if (!data) return <div className="text-xs text-slate-400 py-6 text-center" data-testid="opsday-loading">Loading day-wise expenditure…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="opsday-segment">
      <div className="flex items-center gap-2 flex-wrap px-3 py-2.5 border-b border-slate-200">
        <div className="text-sm font-extrabold text-slate-800 uppercase tracking-wide inline-flex items-center gap-1.5 mr-2">
          <CalendarDays size={14} className="text-orange-600" /> Day-wise Expenditure &amp; Work Done
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="opsday-search" className="border border-slate-300 rounded pl-7 pr-2 py-1.5 text-xs w-56"
            placeholder="Search project code / name…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="opsday-client-filter" className="border border-slate-300 rounded px-2 py-1.5 text-xs"
          value={client} onChange={(e) => setClient(e.target.value)}>
          <option value="">All Clients</option>
          {(data.clients || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="opsday-window" className="border border-slate-300 rounded px-2 py-1.5 text-xs ml-auto"
          value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
          {[7, 14, 30].map((n) => <option key={n} value={n}>Last {n} days</option>)}
        </select>
      </div>

      <div className="max-h-[62vh] overflow-y-auto divide-y divide-slate-100" data-testid="opsday-scroll">
        {filteredDays.map((d) => (
          <div key={d.date} data-testid={`opsday-card-${d.date}`}>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [d.date]: !o[d.date] }))}
              data-testid={`opsday-toggle-${d.date}`}
              className={`w-full flex items-center gap-2 flex-wrap px-3 py-2 text-left hover:bg-slate-50 ${d.shown ? "" : "opacity-60"}`}>
              {open[d.date] ? <ChevronDown size={13} className="text-slate-400 shrink-0" /> : <ChevronRight size={13} className="text-slate-400 shrink-0" />}
              <span className="font-bold text-xs text-slate-900 w-40">{dayLabel(d.date)}</span>
              {d.shown ? (
                <span className="flex items-center gap-1.5 flex-wrap">
                  <Chip label="Work Done" value={inr(d.totals.work_done)} tone="bg-emerald-50 text-emerald-800 border border-emerald-200" testid={`opsday-work-${d.date}`} />
                  <Chip label="Expense" value={inr(d.totals.expense_total)} tone="bg-rose-50 text-rose-800 border border-rose-200" testid={`opsday-exp-${d.date}`} />
                  <span className="text-[10px] text-slate-500">
                    Material {inr(d.totals.material)} · Service PI {inr(d.totals.service_pi)} · Labour {inr(d.totals.labour)}
                  </span>
                  <span className="text-[10px] text-slate-400">· {d.shown} project{d.shown === 1 ? "" : "s"}</span>
                </span>
              ) : (
                <span className="text-[10px] text-slate-400">no activity</span>
              )}
            </button>
            {open[d.date] && d.shown > 0 && (
              <div className="px-3 pb-2.5">
                <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Project</th>
                      <th className="px-2 py-1.5 text-left">Client</th>
                      <th className="px-2 py-1.5 text-right">Material ₹</th>
                      <th className="px-2 py-1.5 text-right">Service PI ₹</th>
                      <th className="px-2 py-1.5 text-right">Labour ₹</th>
                      <th className="px-2 py-1.5 text-right">Total Expense ₹</th>
                      <th className="px-2 py-1.5 text-right">Work Done ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((p) => (
                      <tr key={p.project_id} className="border-t border-slate-100" data-testid={`opsday-row-${d.date}-${p.project_code}`}>
                        <td className="px-2 py-1.5">
                          <span className="font-mono font-bold text-[11px]">{p.project_code}</span>
                          <span className="text-slate-600 ml-1.5">{p.project_name}</span>
                        </td>
                        <td className="px-2 py-1.5 max-w-[140px] truncate" title={p.client_name}>{p.client_name || "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{inr(p.material)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{inr(p.service_pi)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{inr(p.labour)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-800">{inr(p.expense_total)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-800">{inr(p.work_done)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {filteredDays.length === 0 && (
          <div className="px-3 py-8 text-center text-slate-400 text-xs" data-testid="opsday-empty">No days in window.</div>
        )}
      </div>
      <div className="px-3 py-1.5 text-[9px] text-slate-400 border-t border-slate-100">{data.note}</div>
    </div>
  );
}
